import React, { ReactElement, useContext, useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";
import { useDebounce } from "react-use";
import { AppContext } from "AppContext";

import { isNil } from "ramda";

import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { containerMailDomainWBListTable } from "constants/tables";

import IconSelector from "components/shared/images/IconSelector";
import SortingTable from "components/shared/tables/SortingTable";

import { getAllBlacklist } from "redux/handlers/mailDomainContainerHandler";

import {
 getMailDomainsBlacklist,
 getMailDomainsBlacklistNumber
} from "redux/selectors/containerMailSelector";

import { useAppDispatch } from "hooks/reduxHook";

import { IContainerMailDomainData } from "types/api/containerMailDomainApiInterface";

import CreateBlacklist from "../actions/blacklist/CreateBlacklist";
import BlacklistNotFound from "../manageComponents/BlacklistNotFound";

const MailDomainBlacklist = ({
 mailData
}: {
 mailData: IContainerMailDomainData;
}): ReactElement => {
 const dispatch = useAppDispatch();

 /**
  *
EU
: 
0
UK
: 
0
  */

 const { socket } = useContext(AppContext);
 const searchRef = useRef<HTMLInputElement>(null);

 const blackList = useSelector(getMailDomainsBlacklist);
 const blackNumber = useSelector(getMailDomainsBlacklistNumber);

 const [reloadData, setReloadData] = useState<boolean>(true);
 const [searchValue, setSearchValue] = useState<string>("");
 const [listWasEmpty, setListWasEmpty] = useState<boolean>(false);
 const [isLoading, setIsLoading] = useState<boolean>(true);

 useEffect(() => {
  function onBlacklistReload(value: any) {
   if (value?.completed && value?.section === "maildomains_blacklist") {
    setReloadData(true);
   }
  }
  !isNil(socket) && socket.on("docker_data", onBlacklistReload);

  return () => {
   !isNil(socket) && socket.off("docker_data", onBlacklistReload);
  };
 }, [socket]);

 useEffect(() => {
  (async () => {
   if (reloadData) {
    await dispatch(getAllBlacklist(mailData.id, searchRef?.current?.value));
    if (blackNumber === 0) setListWasEmpty(true);
    if (isLoading) setIsLoading(false);
    setReloadData(false);
   }
  })();
 }, [reloadData]);

 useDebounce(
  async () => {
   if (!isLoading) {
    await dispatch(getAllBlacklist(mailData.id, searchRef?.current?.value));
   }
  },
  1000,
  [searchValue]
 );

 return (
  <Paper elevation={1} sx={{ borderRadius: "5px", p: 2 }}>
   <Stack direction="row" spacing={1} alignItems="center">
    <IconSelector icon="PlaylistRemoveIcon" props={{ color: "kxOrange" }} />
    <Typography fontSize={16} ml={1} variant="unboundedTitle">
     <FormattedMessage id="container.maildomains.blacklist" />
    </Typography>
   </Stack>
   <Stack mt={2}>
    {isLoading ? (
     <CircularProgress />
    ) : (blackList && blackList?.length > 0) || !listWasEmpty ? (
     <Stack width="stretch">
      <Stack direction="row" justifyContent="space-between" alignItems="center">
       <TextField
        size="small"
        autoComplete="new-password"
        label={<FormattedMessage id="container.maildomains.searchRule" />}
        inputRef={searchRef}
        InputLabelProps={{ shrink: true }}
        InputProps={{
         startAdornment: <IconSelector icon="SearchIcon" props={{ color: "disabled" }} />,
         autoComplete: "off"
        }}
        onChange={({ currentTarget }) => setSearchValue(currentTarget.value)}
       />
       <Stack direction="row" spacing={1}>
        <CreateBlacklist id={mailData.id} />
       </Stack>
      </Stack>
      <Paper elevation={1} sx={{ borderRadius: "5px", mt: 1 }}>
       <SortingTable
        tableData={containerMailDomainWBListTable}
        maxWidth={650}
        caller="container_mail_blacklist"
        listToShow={blackList}
       />
      </Paper>
     </Stack>
    ) : (
     <BlacklistNotFound id={mailData.id} />
    )}
   </Stack>
  </Paper>
 );
};

export default MailDomainBlacklist;
